import type { User } from 'firebase/auth';
import CryptoJS from 'crypto-js';

/**
 * The address a signed-in user acts under, for every email-keyed read in the console
 * (the access guard, the dashboard's event scan).
 *
 * The Firebase Auth record's email is the normal source. A master account minted by
 * custom token — the email-code login and the preview hand-off — can have NONE: when the
 * address is already owned by another account (an older password login), the server's
 * back-fill of the record is refused by Firebase and only logged. Both minters put the
 * proven address in the token as the `email` claim (with `email_verified: true`), and the
 * server already trusts that claim (claimRoles grants from it), so it is the fallback here.
 *
 * One helper, so the guard and the dashboard cannot disagree about who is signed in.
 * (2026-10-10: a preview requester with an older password account signed in through the
 * hand-off, claimRoles granted admin, and the guard bounced them to login.)
 */
export function pickSignedInEmail(
  recordEmail: string | null | undefined,
  claims: Record<string, unknown> | null | undefined,
): string | null {
  const record = typeof recordEmail === 'string' ? recordEmail.trim() : '';
  if (record) return record;
  const claim = claims ? claims['email'] : undefined;
  return typeof claim === 'string' && claim.trim() ? claim.trim() : null;
}

/**
 * The id the platform keys a person by: md5 of the lowercased, trimmed email, as the app
 * (`PersonIdentity.EmailHash`) and the functions (`md5LowerTrim`, `personIdFromEmail`) compute
 * it. Participant docs, permission seeds and read-state are all keyed by it.
 */
export function emailKey(email: string): string {
  return CryptoJS.MD5(email.trim().toLowerCase()).toString();
}

/** Who is signed in: the Firebase user, the address they act under, and that address's key. */
export interface SignedInPerson {
  user: User;
  email: string | null;
  key: string | null;
}

/**
 * The signed-in person, for every page that reads something keyed by the person. Never hash
 * `user.email` directly: a custom-token account (email-code login, preview hand-off) has none,
 * and the console bounced organizers to the login page when contribution details hashed it
 * (2026-10-11). tools/no-email-key-in-components.mjs enforces this.
 */
export async function signedInPerson(user: User): Promise<SignedInPerson> {
  const email = await signedInEmail(user);
  return { user, email, key: email ? emailKey(email) : null };
}

/** Resolves the record email, else the token's `email` claim; null when there is neither. */
export async function signedInEmail(user: User | null | undefined): Promise<string | null> {
  if (!user) return null;
  if (user.email) return pickSignedInEmail(user.email, null);
  let claims: Record<string, unknown> | null = null;
  try {
    claims = (await user.getIdTokenResult()).claims as Record<string, unknown>;
  } catch {
    claims = null;
  }
  return pickSignedInEmail(null, claims);
}
